import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Sparkles, Trophy, CheckCircle2, Clock, XCircle, Send, CalendarDays, FileCheck2, GraduationCap, ChevronRight, ChevronDown, Bell, Wallet, MessageSquareText, ClipboardList, ShieldCheck, Radio, CalendarClock, HeartHandshake, BookMarked } from "lucide-react";
import { toast } from "sonner";
import { userApi, apiError, logout, getUser, mediaUrl, compromisoApi, connectApi, isParentView } from "../lib/api";
import { financeApi } from "../lib/api";
import { usePWA } from "../hooks/usePWA";
import { subscribeToPush } from "../lib/push";
import { NotificationBell } from "../components/NotificationBell";
import { ParentViewBar } from "../components/ParentViewBar";
import { AnaPanel } from "../components/ana/AnaPanel";

const STATUS = {
  en_revision: { label: "En revisión", cls: "bg-[#FBF0CE] text-[#B9860B]", icon: Clock },
  valida: { label: "Válida", cls: "bg-[#E2F6EB] text-[#22A860]", icon: CheckCircle2 },
  rechazada: { label: "Rechazada", cls: "bg-[#FCE4E4] text-[#E23B3B]", icon: XCircle },
};

export default function UserDashboard() {
  const navigate = useNavigate();
  const user = getUser();
  const [today, setToday] = useState(null);
  const [evData, setEvData] = useState({ puntos: 0, evidencias: [] });
  const [comment, setComment] = useState({});
  const [editing, setEditing] = useState({});
  const [sending, setSending] = useState(false);
  const [evOpen, setEvOpen] = useState(false);
  const { requestNotifications, notify, notifPermission } = usePWA();
  const [finEligible, setFinEligible] = useState(false);
  const [compEnabled, setCompEnabled] = useState(false);
  const [badges, setBadges] = useState({});
  const isParent = user?.programa === "Padres y Madres";
  const readOnly = isParentView();

  const badgePill = (key) => {
    const b = badges[key];
    if (!b) return null;
    const tones = {
      gold: "bg-[var(--gold)] text-[#0A1C30]",
      blue: "bg-[#2F5BEA] text-white",
      red: "bg-[#E5484D] text-white",
      green: "bg-[#22A860] text-white",
    };
    const text = b.count ? `${b.count} ${b.label || ""}`.trim() : (b.label || "Nuevo");
    return (
      <span data-testid={`badge-${key}`} className={`ml-2 align-middle inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-extrabold ${tones[b.tone] || tones.gold}`}>
        {text}
      </span>
    );
  };

  const enableNotifications = async () => {
    try {
      const perm = await requestNotifications();
      if (perm === "granted") {
        await subscribeToPush();
        notify("SUPERA", { body: "¡Listo! Te avisaremos de tus clases y novedades." });
        toast.success("Notificaciones activadas.");
      } else {
        toast.error("Activa los permisos de notificaciones en tu navegador.");
      }
    } catch (e) {
      toast.error("No se pudieron activar las notificaciones.");
    }
  };

  const load = async () => {
    try {
      const [t, e] = await Promise.all([userApi.reflexionesToday(), userApi.myEvidencias()]);
      setToday(t);
      setEvData(e);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  useEffect(() => { load(); subscribeToPush(); financeApi.status().then((s) => setFinEligible(s.eligible)).catch(() => {}); compromisoApi.me().then((c) => setCompEnabled(!!c.enabled)).catch(() => {}); userApi.dashboardBadges().then(setBadges).catch(() => {}); /* eslint-disable-next-line */ }, []);

  const signOut = () => { logout(); navigate("/"); };

  const submit = async (reflexionId) => {
    const text = (comment[reflexionId] || "").trim();
    if (text.length < 3) { toast.error("Escribe tu aprendizaje sobre la reflexión."); return; }
    setSending(true);
    try {
      const res = await userApi.submitEvidencia(reflexionId, text);
      toast.success(res.message || "Evidencia enviada y en proceso de revisión.");
      setComment((c) => ({ ...c, [reflexionId]: "" }));
      setEditing((e) => ({ ...e, [reflexionId]: false }));
      await load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  const items = today?.items || [];

  return (
    <div className="min-h-screen bg-cream">
      {/* Top bar */}
      <header className="bg-navy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between">
          <img src="/logo-supera-light.png" alt="Supera" className="h-9 sm:h-11 w-auto object-contain" />
          <div className="flex items-center gap-2 sm:gap-3">
            <NotificationBell />
            {notifPermission !== "granted" && (
              <button data-testid="enable-notif-btn" onClick={enableNotifications} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold px-3 sm:px-3.5 py-1.5 text-sm transition-colors">
                <Bell className="w-4 h-4 text-gold" /> <span className="hidden sm:inline">Activar avisos</span>
              </button>
            )}
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--gold)] text-[#0A1C30] font-extrabold px-3 sm:px-4 py-1.5 text-sm">
              <Trophy className="w-4 h-4" /> {evData.puntos} pts
            </span>
            <button data-testid="profile-link-btn" onClick={() => navigate("/mi-perfil")} title="Mi perfil" className="w-9 h-9 sm:w-10 sm:h-10 rounded-full overflow-hidden bg-white/10 ring-2 ring-white/20 hover:ring-gold flex items-center justify-center transition-all shrink-0">
              {mediaUrl(user?.avatar_url) ? (
                <img src={mediaUrl(user?.avatar_url)} alt="Perfil" className="w-full h-full object-cover" />
              ) : (
                <span className="text-sm font-extrabold text-gold">{`${(user?.nombre || "L")[0] || ""}${(user?.apellidos || "")[0] || ""}`.toUpperCase()}</span>
              )}
            </button>
            <button onClick={signOut} title="Salir" className="inline-flex items-center gap-1.5 text-white/70 hover:text-white text-sm font-semibold transition-colors">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Salir</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <div className="mb-8"><AnaPanel /></div>
        <p className="text-xs font-extrabold tracking-[0.25em] text-[#B99B2E]">HOLA, {(user?.name || user?.nombre || "LÍDER").toUpperCase()}</p>
        <h1 className="mt-2 text-3xl md:text-4xl font-extrabold text-[#0A1C30]">
          Reflexión <span className="font-serif-it text-[#E9A400]">Supera</span> de hoy
        </h1>
        {user?.programa && (
          <div data-testid="dashboard-programa-grupo" className="mt-3 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center rounded-full bg-[#0A1C30] text-white text-xs font-bold px-3 py-1">{user.programa}</span>
            <span className="inline-flex items-center rounded-full bg-[#E9A400] text-[#0A1C30] text-xs font-bold px-3 py-1">{user?.grupo ? `Grupo ${user.grupo}` : "Grupo sin asignar"}</span>
          </div>
        )}

        <ParentViewBar />

        {/* Today's reflections — mostrada al inicio, justo debajo del encabezado */}
        <div className="mt-6 space-y-6" data-testid="reflexiones-hoy">
          {today && items.length === 0 && (
            <div className="bg-white rounded-3xl ring-1 ring-black/5 p-10 text-center">
              <CalendarDays className="w-10 h-10 text-[#C7CDD6] mx-auto" />
              <p className="mt-3 text-[#6B7688] font-semibold">Hoy no tienes una reflexión asignada. ¡Vuelve mañana!</p>
            </div>
          )}

          {items.map(({ reflexion, evidencia }) => {
            const r = reflexion;
            const showForm = !evidencia || editing[r.id];
            const st = evidencia ? STATUS[evidencia.status] : null;
            return (
              <div key={r.id} className="bg-white rounded-3xl ring-1 ring-black/5 overflow-hidden">
                <div className="bg-navy px-6 py-3 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-gold" />
                  <span className="text-white font-bold text-sm">{r.fecha}</span>
                </div>
                <div className="p-6 md:p-8">
                  {r.titulo && <h2 className="text-xl font-extrabold text-[#0A1C30] mb-3">{r.titulo}</h2>}
                  {r.tipo === "imagen" && r.imagen_url ? (
                    <img src={r.imagen_url} alt={r.titulo || "Reflexión"} className="w-full max-h-96 object-contain rounded-2xl bg-[#F4F6F9]" />
                  ) : null}
                  {r.texto && <p className="mt-3 text-[#2A3446] leading-relaxed whitespace-pre-wrap">{r.texto}</p>}

                  {/* Status banner */}
                  {evidencia && !editing[r.id] && (
                    <div className={`mt-6 rounded-2xl p-4 ${st.cls}`}>
                      <div className="flex items-center gap-2 font-extrabold">
                        <st.icon className="w-5 h-5" /> {st.label}
                      </div>
                      {evidencia.status === "en_revision" && (
                        <p className="text-sm mt-1">Evidencia enviada y en proceso de revisión.</p>
                      )}
                      {evidencia.status === "rechazada" && (
                        <>
                          <p className="text-sm mt-1 font-semibold">{evidencia.rejection_text}</p>
                          {evidencia.feedback && <p className="text-sm mt-1 italic">“{evidencia.feedback}”</p>}
                          {!readOnly && (
                            <button onClick={() => setEditing((e) => ({ ...e, [r.id]: true }))} className="mt-3 rounded-xl bg-[#0A1C30] text-white font-bold px-4 py-2 text-sm hover:bg-[#0E2740] transition-colors">
                              Volver a comentar
                            </button>
                          )}
                        </>
                      )}
                      {evidencia.status === "valida" && evidencia.feedback && (
                        <p className="text-sm mt-1 italic">“{evidencia.feedback}”</p>
                      )}
                    </div>
                  )}

                  {/* Comment form (invitation) */}
                  {showForm && !readOnly && (
                    <div className="mt-6 rounded-2xl bg-[#FAF8F1] p-5 ring-1 ring-[var(--gold)]/40">
                      <p className="font-extrabold text-[#0A1C30]">💬 ¿Qué aprendizaje te deja esta reflexión?</p>
                      <p className="text-sm text-[#6B7688] mt-0.5">Comparte tu comentario para registrar tu evidencia y sumar un punto.</p>
                      <textarea
                        value={comment[r.id] || ""}
                        onChange={(e) => setComment((c) => ({ ...c, [r.id]: e.target.value }))}
                        placeholder="Escribe tu aprendizaje..."
                        className="mt-3 w-full min-h-[110px] rounded-xl border border-black/10 bg-white p-3 text-sm text-[#0A1C30] outline-none focus:ring-2 focus:ring-[var(--gold)] transition-shadow"
                      />
                      <button onClick={() => submit(r.id)} disabled={sending} className="mt-3 inline-flex items-center gap-2 rounded-xl bg-[var(--gold)] hover:bg-[var(--gold-soft)] text-[#0A1C30] font-extrabold px-5 py-2.5 transition-colors disabled:opacity-60">
                        <Send className="w-4 h-4" /> {sending ? "Enviando..." : "Enviar mi evidencia"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Campus Supera CTA */}
        <button onClick={() => navigate("/campus")} className="mt-6 w-full group flex items-center gap-4 rounded-3xl bg-gradient-to-br from-[#0e2c52] via-[#0A1C30] to-[#16324f] p-5 md:p-6 text-left hover:shadow-xl transition-all relative overflow-hidden">
          <span className="absolute -right-8 -top-8 w-28 h-28 rounded-full bg-[#2F5BEA]/20 blur-2xl" />
          <span className="w-14 h-14 rounded-2xl bg-white/10 ring-1 ring-white/15 flex items-center justify-center flex-shrink-0">
            <GraduationCap className="w-7 h-7 text-[var(--gold)]" />
          </span>
          <span className="flex-1 relative">
            <span className="block text-lg font-extrabold text-white">Campus Supera {badgePill("campus")}</span>
            <span className="block text-sm text-white/60">Entra a tus materias, clases en vivo, grabaciones y recursos.</span>
          </span>
          <ChevronRight className="w-6 h-6 text-gold transition-transform group-hover:translate-x-1" />
        </button>

        <button data-testid="capacitaciones-cta" onClick={() => navigate("/capacitaciones")} className="mt-4 w-full group flex items-center gap-4 rounded-3xl bg-gradient-to-br from-[#2a2410] via-[#0A1C30] to-[#16324f] p-5 md:p-6 text-left hover:shadow-xl transition-all relative overflow-hidden">
          <span className="absolute -right-8 -top-8 w-28 h-28 rounded-full bg-[#F5C518]/15 blur-2xl" />
          <span className="w-14 h-14 rounded-2xl bg-white/10 ring-1 ring-white/15 flex items-center justify-center flex-shrink-0">
            <BookMarked className="w-7 h-7 text-[var(--gold)]" />
          </span>
          <span className="flex-1 relative">
            <span className="block text-lg font-extrabold text-white">Capacitación Supera</span>
            <span className="block text-sm text-white/60">Domina cada herramienta de la app y aprueba tus módulos de liderazgo.</span>
          </span>
          <ChevronRight className="w-6 h-6 text-gold transition-transform group-hover:translate-x-1" />
        </button>

        <button data-testid="superax-cta" onClick={() => navigate("/superax")} className="mt-4 w-full group flex items-center gap-4 rounded-3xl bg-gradient-to-br from-[#1a1030] via-[#0A1C30] to-[#16324f] p-5 md:p-6 text-left hover:shadow-xl transition-all relative overflow-hidden">
          <span className="absolute -right-8 -top-8 w-28 h-28 rounded-full bg-[#E9A400]/15 blur-2xl" />
          <span className="w-14 h-14 rounded-2xl bg-white/10 ring-1 ring-white/15 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-7 h-7 text-[var(--gold)]" />
          </span>
          <span className="flex-1 relative">
            <span className="block text-lg font-extrabold text-white">Supera <span className="italic text-[var(--gold)]">X</span></span>
            <span className="block text-sm text-white/60">En Supera no solo aprendes liderazgo, lo vives y lo eXperimentas.</span>
          </span>
          <ChevronRight className="w-6 h-6 text-gold transition-transform group-hover:translate-x-1" />
        </button>

        {/* Supera Nexus CTA */}
        <button data-testid="nexus-cta" onClick={() => navigate("/nexus")} className="mt-4 w-full group flex items-center gap-4 rounded-3xl bg-gradient-to-br from-[#1E6F5C] via-[#0A1C30] to-[#16324f] p-5 md:p-6 text-left hover:shadow-xl transition-all relative overflow-hidden">
          <span className="absolute -right-8 -top-8 w-28 h-28 rounded-full bg-[var(--gold)]/15 blur-2xl" />
          <span className="w-14 h-14 rounded-2xl bg-white/10 ring-1 ring-white/15 flex items-center justify-center flex-shrink-0">
            <CalendarClock className="w-7 h-7 text-[var(--gold)]" />
          </span>
          <span className="flex-1 relative">
            <span className="block text-lg font-extrabold text-white">Supera <span className="italic text-[var(--gold)]">Nexus</span></span>
            <span className="block text-sm text-white/60">Agenda tu reunión: Diálogo Mensual, Seguimiento Personalizado o Reunión Especial.</span>
          </span>
          <ChevronRight className="w-6 h-6 text-gold transition-transform group-hover:translate-x-1" />
        </button>

        {/* Puntos Graduación CTA */}
        <button data-testid="graduacion-cta" onClick={() => navigate("/graduacion")} className="mt-4 w-full group flex items-center gap-4 rounded-3xl bg-gradient-to-br from-[#3a2c0c] via-[#0A1C30] to-[#16324f] p-5 md:p-6 text-left hover:shadow-xl transition-all relative overflow-hidden">
          <span className="absolute -right-8 -top-8 w-28 h-28 rounded-full bg-[var(--gold)]/15 blur-2xl" />
          <span className="w-14 h-14 rounded-2xl bg-white/10 ring-1 ring-white/15 flex items-center justify-center flex-shrink-0">
            <Trophy className="w-7 h-7 text-[var(--gold)]" />
          </span>
          <span className="flex-1 relative">
            <span className="block text-lg font-extrabold text-white">Puntos Graduación Supera</span>
            <span className="block text-sm text-white/60">Sube evidencias, suma puntos y avanza en el ranking hacia tu graduación.</span>
          </span>
          <ChevronRight className="w-6 h-6 text-gold transition-transform group-hover:translate-x-1" />
        </button>

        {/* Chat Privado Supera CTA */}
        <button data-testid="chat-cta" onClick={() => navigate("/chat")} className="mt-4 w-full group flex items-center gap-4 rounded-3xl bg-gradient-to-br from-[#0e2c52] via-[#0A1C30] to-[#16324f] p-5 md:p-6 text-left hover:shadow-xl transition-all relative overflow-hidden">
          <span className="absolute -right-8 -top-8 w-28 h-28 rounded-full bg-[#2F5BEA]/20 blur-2xl" />
          <span className="w-14 h-14 rounded-2xl bg-white/10 ring-1 ring-white/15 flex items-center justify-center flex-shrink-0">
            <MessageSquareText className="w-7 h-7 text-[var(--gold)]" />
          </span>
          <span className="flex-1 relative">
            <span className="block text-lg font-extrabold text-white">Chat Privado Supera {badgePill("chat")}</span>
            <span className="block text-sm text-white/60">Comunícate en tiempo real con el staff Supera o con tu asistente virtual.</span>
          </span>
          <ChevronRight className="w-6 h-6 text-gold transition-transform group-hover:translate-x-1" />
        </button>

        {compEnabled && (
          <button data-testid="compromiso-cta" onClick={() => navigate("/compromisos")} className="mt-4 w-full group flex items-center gap-4 rounded-3xl bg-gradient-to-br from-[#0A1C30] to-[#16324f] p-5 md:p-6 text-left hover:shadow-xl transition-all relative overflow-hidden">
            <span className="absolute -right-8 -top-8 w-28 h-28 rounded-full bg-[var(--gold)]/15 blur-2xl" />
            <span className="w-14 h-14 rounded-2xl bg-white/10 ring-1 ring-white/15 flex items-center justify-center flex-shrink-0">
              <ClipboardList className="w-7 h-7 text-[var(--gold)]" />
            </span>
            <span className="flex-1 relative">
              <span className="block text-lg font-extrabold text-white">Hoja de Compromisos Supera {badgePill("compromisos")}</span>
              <span className="block text-sm text-white/60">Marca tus actividades semanales y forja tu liderazgo con acciones concretas.</span>
            </span>
            <ChevronRight className="w-6 h-6 text-[var(--gold)] transition-transform group-hover:translate-x-1" />
          </button>
        )}

        {isParent && (
          <button data-testid="validaciones-cta" onClick={() => navigate("/validaciones")} className="mt-4 w-full group flex items-center gap-4 rounded-3xl bg-gradient-to-br from-[#123a2a] via-[#0A1C30] to-[#16324f] p-5 md:p-6 text-left hover:shadow-xl transition-all relative overflow-hidden">
            <span className="absolute -right-8 -top-8 w-28 h-28 rounded-full bg-[#22A860]/20 blur-2xl" />
            <span className="w-14 h-14 rounded-2xl bg-white/10 ring-1 ring-white/15 flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-7 h-7 text-[var(--gold)]" />
            </span>
            <span className="flex-1 relative">
              <span className="block text-lg font-extrabold text-white">Validaciones Supera {badgePill("validaciones")}</span>
              <span className="block text-sm text-white/60">Valida las actividades y entregas de la Hoja de Compromisos de tu hijo/a.</span>
            </span>
            <ChevronRight className="w-6 h-6 text-gold transition-transform group-hover:translate-x-1" />
          </button>
        )}

        {finEligible && (
          <button data-testid="financiamiento-cta" onClick={() => navigate("/financiamiento")} className="mt-4 w-full group flex items-center gap-4 rounded-3xl bg-gradient-to-br from-[#2a2410] via-[#0A1C30] to-[#16324f] p-5 md:p-6 text-left hover:shadow-xl transition-all relative overflow-hidden">
            <span className="absolute -right-8 -top-8 w-28 h-28 rounded-full bg-[var(--gold)]/15 blur-2xl" />
            <span className="w-14 h-14 rounded-2xl bg-white/10 ring-1 ring-white/15 flex items-center justify-center flex-shrink-0">
              <Wallet className="w-7 h-7 text-[var(--gold)]" />
            </span>
            <span className="flex-1 relative">
              <span className="block text-lg font-extrabold text-white">Financiamiento Educativo {badgePill("financiamiento")}</span>
              <span className="block text-sm text-white/60">Revisa tu estado de cuenta, realiza tus pagos y consulta la ayuda financiera familiar.</span>
            </span>
            <ChevronRight className="w-6 h-6 text-gold transition-transform group-hover:translate-x-1" />
          </button>
        )}

        {isParent && (
          <button data-testid="padres-cta" onClick={() => navigate("/padres")} className="mt-4 w-full group flex items-center gap-4 rounded-3xl bg-gradient-to-br from-[#3a1030] via-[#0A1C30] to-[#16324f] p-5 md:p-6 text-left hover:shadow-xl transition-all relative overflow-hidden">
            <span className="absolute -right-8 -top-8 w-28 h-28 rounded-full bg-[#E0559B]/20 blur-2xl" />
            <span className="w-14 h-14 rounded-2xl bg-white/10 ring-1 ring-white/15 flex items-center justify-center flex-shrink-0">
              <HeartHandshake className="w-7 h-7 text-[var(--gold)]" />
            </span>
            <span className="flex-1 relative">
              <span className="block text-lg font-extrabold text-white">Padres y Madres Supera</span>
              <span className="block text-sm text-white/60">Acompaña todo el desarrollo de tu hijo/a: sesiones, podcast, libro del mes, evaluaciones y su desarrollo personalizado.</span>
            </span>
            <ChevronRight className="w-6 h-6 text-[var(--gold)] transition-transform group-hover:translate-x-1" />
          </button>
        )}

        {/* Evidencias realizadas (colapsable para mantener el dashboard ordenado) */}
        <div className="mt-12">
          <button data-testid="evidencias-toggle" onClick={() => setEvOpen((o) => !o)} aria-expanded={evOpen} className="w-full flex items-center justify-between gap-2 rounded-2xl bg-white ring-1 ring-black/5 px-5 py-4 hover:shadow-md transition-all">
            <span className="flex items-center gap-2">
              <FileCheck2 className="w-6 h-6 text-[#0A1C30]" />
              <span className="text-xl font-extrabold text-[#0A1C30]">Evidencias realizadas</span>
              <span className="inline-flex items-center justify-center min-w-[26px] h-6 px-2 rounded-full bg-[#EDF0F4] text-[#0A1C30] text-xs font-extrabold">{evData.evidencias.length}</span>
            </span>
            <ChevronDown className={`w-6 h-6 text-[#6B7688] transition-transform ${evOpen ? "rotate-180" : ""}`} />
          </button>
          {evOpen && (
            <div className="mt-4 space-y-3" data-testid="evidencias-list">
              {evData.evidencias.length === 0 && <p className="text-[#6B7688] text-sm px-1">Aún no has registrado evidencias.</p>}
              {evData.evidencias.map((ev) => {
                const st = STATUS[ev.status];
                return (
                  <div key={ev.id} className="bg-white rounded-2xl ring-1 ring-black/5 p-5">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-xs font-bold text-[#6B7688]">{ev.reflexion_fecha}</span>
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${st.cls}`}>
                        <st.icon className="w-3.5 h-3.5" /> {st.label}
                      </span>
                    </div>
                    <p className="mt-2 text-[#2A3446] text-sm">{ev.comentario}</p>
                    {ev.status === "rechazada" && ev.rejection_text && (
                      <p className="mt-2 text-xs font-semibold text-[#E23B3B]">{ev.rejection_text}</p>
                    )}
                    {ev.feedback && <p className="mt-1 text-xs italic text-[#6B7688]">Retroalimentación: “{ev.feedback}”</p>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
